import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import logger from '../../utils/logger';
import { checkPasswordStrengthFn } from '../registration/passwordUtils';

export const getStrengthColor = (score, type) => {
  if (score <= 1) return type === 'text' ? 'text-red-400' : 'bg-red-500';
  if (score <= 3) return type === 'text' ? 'text-yellow-400' : 'bg-yellow-500';
  return type === 'text' ? 'text-green-400' : 'bg-green-500';
};

export const useResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!token) setError('Invalid or missing reset token'); }, [token]);

  const strength = useMemo(() => {
    if (!newPassword) return { score: 0, message: '', checks: {} };
    const s = checkPasswordStrengthFn(newPassword);
    return { score: s.score, message: s.label, checks: s.checks };
  }, [newPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (strength.score < 4) {
      setError('Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number and a special character.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword }),
      });
      if (res.ok) { setSuccess(true); setTimeout(() => navigate('/admin/login'), 3000); }
      else { const data = await res.json(); setError(data.detail || 'Failed to reset password. The link may have expired.'); }
    } catch (err) { logger.error(err); setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  return {
    newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    showNew, setShowNew, showConfirm, setShowConfirm,
    loading, success, error, token, strength, navigate, handleSubmit,
  };
};
